import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";

const RISK_COLORS = {
  normal: "text-brand-text",
  high: "text-amber-300",
  critical: "text-red-300",
};

export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState("");
  const [risk, setRisk] = useState("");
  const [resource, setResource] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (action) params.action = action;
    if (risk) params.risk_level = risk;
    if (resource) params.resource_type = resource;
    const { data } = await api.get("/admin/audit-logs", { params });
    setLogs(data);
  }, [action, risk, resource]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 py-10" data-testid="admin-audit-logs-page">
        <div className="mb-6">
          <div className="overline mb-2">Governance</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">Audit Logs</h1>
          <p className="mt-2 text-brand-muted text-sm max-w-2xl">Immutable record of significant actions. Not editable or deletable through the admin interface.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Input placeholder="Action filter (e.g. batch.approve)" value={action} onChange={(e) => setAction(e.target.value)} className="max-w-xs" data-testid="audit-filter-action" />
          <Select value={risk || "__all"} onValueChange={(v) => setRisk(v === "__all" ? "" : v)}>
            <SelectTrigger className="max-w-[200px]" data-testid="audit-filter-risk"><SelectValue placeholder="Risk level" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All risk levels</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
          <Select value={resource || "__all"} onValueChange={(v) => setResource(v === "__all" ? "" : v)}>
            <SelectTrigger className="max-w-[220px]" data-testid="audit-filter-resource"><SelectValue placeholder="Resource" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All resources</SelectItem>
              <SelectItem value="batch">Batch</SelectItem>
              <SelectItem value="coa">COA</SelectItem>
              <SelectItem value="account_application">Account application</SelectItem>
              <SelectItem value="restriction">Restriction</SelectItem>
              <SelectItem value="recall">Recall</SelectItem>
              <SelectItem value="settings">Settings</SelectItem>
              <SelectItem value="wholesale_lead">Wholesale lead</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border border-brand-border">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-white overline text-[10px]">
            <div className="col-span-3">When</div>
            <div className="col-span-3">Action</div>
            <div className="col-span-2">Resource</div>
            <div className="col-span-2">Actor</div>
            <div className="col-span-1">Risk</div>
            <div className="col-span-1">IP</div>
          </div>
          <div className="divide-y divide-brand-border">
            {logs.length === 0 ? (
              <div className="p-16 text-center text-sm text-brand-muted bg-white">No audit records match the current filters.</div>
            ) : logs.map((l) => (
              <div key={l.id} className="grid grid-cols-12 gap-4 px-6 py-3 bg-white text-xs items-center">
                <div className="col-span-3 font-mono text-brand-muted">{new Date(l.created_at).toLocaleString()}</div>
                <div className="col-span-3 font-mono text-brand-navy">{l.action}</div>
                <div className="col-span-2 font-mono text-brand-muted">{l.resource_type}/{(l.resource_id || "").slice(0,8)}</div>
                <div className="col-span-2 font-mono text-brand-muted">{l.actor_role || "—"}</div>
                <div className={`col-span-1 font-mono uppercase text-[10px] ${RISK_COLORS[l.risk_level] || "text-brand-muted"}`}>{l.risk_level}</div>
                <div className="col-span-1 font-mono text-slate-600">{l.ip_address || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminGuard>
  );
}
